/**
 * 실전 모의고사 / 과목별 집중 연습 성취도 저장 유틸리티
 *
 * 배경: 기존 learningProgressStorage 는 진단(diagnostic_) 데이터를 전체 통계/과목/패턴에서
 *       제외하도록 설계되어 있다. 모의고사·과목별 연습은 DiagnosticTest 를 재사용하므로
 *       그 경로로는 성취도가 집계되지 않는다. 따라서 새 연습 모드의 성취도를 별도로 기록/조회한다.
 *
 * 저장 키: practiceAchievements_{userId}
 */

const KEY = (userId) => `practiceAchievements_${userId}`;
const MAX_MOCK_HISTORY = 30;

const emptyData = () => ({ mockExams: [], subjects: {} });

// 성취도 데이터 조회
export const getPracticeAchievements = (userId) => {
  if (!userId) return emptyData();
  try {
    const raw = localStorage.getItem(KEY(userId));
    if (!raw) return emptyData();
    const data = JSON.parse(raw);
    return {
      mockExams: Array.isArray(data.mockExams) ? data.mockExams : [],
      subjects: data.subjects && typeof data.subjects === 'object' ? data.subjects : {},
    };
  } catch (e) {
    console.error('연습 성취도 로딩 실패:', e);
    return emptyData();
  }
};

const save = (userId, data) => {
  try {
    localStorage.setItem(KEY(userId), JSON.stringify(data));
    return true;
  } catch (e) {
    console.error('연습 성취도 저장 실패:', e);
    return false;
  }
};

/**
 * 연습 완료 결과 기록
 * @param {string} userId
 * @param {object} r
 *   - mode: 'mock' | 'subject'        (실전 모의고사 / 과목별 집중)
 *   - cert: 영문 자격증 키
 *   - certTitle: 표시용 자격증명
 *   - focusSubject: 과목 모드일 때 과목명
 *   - score: 0~100 점수
 *   - correctCount, totalQuestions, totalTime(초)
 *   - subjectBreakdown: { [subject]: { correct, total } }  (답변한 문항 기준)
 */
export const recordPracticeResult = (userId, r) => {
  if (!userId || !r) return;
  const data = getPracticeAchievements(userId);
  const today = new Date().toISOString().split('T')[0];
  const ts = new Date().toISOString();

  // 1) 과목별 성취도 누적
  Object.entries(r.subjectBreakdown || {}).forEach(([subject, stat]) => {
    const correct = stat?.correct || 0;
    const total = stat?.total || 0;
    if (!total) return;

    if (!data.subjects[r.cert]) data.subjects[r.cert] = {};
    const prev = data.subjects[r.cert][subject] || {
      attempts: 0,
      totalAnswered: 0,
      totalCorrect: 0,
      accuracy: 0,
      bestAccuracy: 0,
      lastStudied: null,
    };

    prev.attempts += 1;
    prev.totalAnswered += total;
    prev.totalCorrect += correct;
    prev.accuracy = Math.round((prev.totalCorrect / prev.totalAnswered) * 100);
    prev.bestAccuracy = Math.max(prev.bestAccuracy, Math.round((correct / total) * 100));
    prev.lastStudied = today;

    data.subjects[r.cert][subject] = prev;
  });

  // 2) 모의고사 이력 (최근순)
  if (r.mode === 'mock') {
    data.mockExams.unshift({
      cert: r.cert,
      certTitle: r.certTitle,
      score: r.score,
      correctCount: r.correctCount,
      totalQuestions: r.totalQuestions,
      totalTime: r.totalTime,
      subjectBreakdown: r.subjectBreakdown || {},
      date: today,
      timestamp: ts,
    });
    data.mockExams = data.mockExams.slice(0, MAX_MOCK_HISTORY);
  }

  save(userId, data);
};

// 과목 숙련도 라벨/색상 (자격증 합격 기준 60점 참고)
export const masteryLabel = (accuracy) => {
  if (accuracy >= 80) return { label: '우수', color: 'green' };
  if (accuracy >= 60) return { label: '양호', color: 'blue' };
  if (accuracy >= 40) return { label: '보통', color: 'amber' };
  return { label: '취약', color: 'red' };
};

// 모의고사 요약 통계
export const getMockSummary = (mockExams = []) => {
  if (!mockExams.length) return null;
  const scores = mockExams.map((m) => m.score);
  const latest = mockExams[0].score; // 최근순 저장
  const best = Math.max(...scores);
  const average = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  return {
    latest,
    best,
    average,
    attempts: mockExams.length,
    passLikely: average >= 60,
  };
};
